import { BLOCKCHAIN_ADDRESS_ACTIONS } from './const'
import { ActionTypes, BlockchainAddress, FinancialInstitution } from './types'
import { Dispatch } from '~/app/store/types'
import { del, ENDPOINTS, get, post } from '~/api'
import { keysToCamel, objectToQueryParams } from '~/helpers/helpers'

export const setBlockchainAddressList = (blockchainAddressList: BlockchainAddress[]) => (
  dispatch: Dispatch<ActionTypes>
) => {
  dispatch({
    type: BLOCKCHAIN_ADDRESS_ACTIONS.SET_BLOCKCHAIN_ADDRESS_LIST,
    blockchainAddressList,
  })
}

export const setMaxAddressAmount = (maxAddressAmount: number) => (
  dispatch: Dispatch<ActionTypes>
) => {
  dispatch({
    type: BLOCKCHAIN_ADDRESS_ACTIONS.SET_MAX_ADDRESS_AMOUNT,
    maxAddressAmount,
  })
}

export const setAddressQuota = (addressQuota: number) => (dispatch: Dispatch<ActionTypes>) => {
  dispatch({
    type: BLOCKCHAIN_ADDRESS_ACTIONS.SET_ADDRESS_QUOTA,
    addressQuota,
  })
}

export const setFIList = (fiList: FinancialInstitution[]) => (dispatch: Dispatch<ActionTypes>) => {
  dispatch({
    type: BLOCKCHAIN_ADDRESS_ACTIONS.SET_FI_LIST,
    fiList,
  })
}

export const updateBlockchainAddress = (blockchainAddress: BlockchainAddress) => (
  dispatch: Dispatch<ActionTypes>
) => {
  dispatch({
    type: BLOCKCHAIN_ADDRESS_ACTIONS.UPDATE_BLOCKCHAIN_ADDRESS,
    blockchainAddress,
  })
}

export const fetchBlockchainAddresses = (accountId: number, successCallback?: () => void) => async (
  dispatch: Dispatch<ActionTypes>
) => {
  const subRoute = `/${accountId}`

  const resp = await get(ENDPOINTS.API_V3_STABLECOIN_ADDRESSES, subRoute)
  if (resp.data.address_list) {
    const respList = resp.data.address_list as []
    const addressList: BlockchainAddress[] = respList.map(blockchainAddress =>
      keysToCamel(blockchainAddress)
    )
    dispatch(setBlockchainAddressList(addressList))
  }

  if (resp.data.fi_list) {
    dispatch(setFIList(resp.data.fi_list as FinancialInstitution[]))
  }

  if (resp.data.max_address_amount && !Number.isNaN(resp.data.max_address_amount)) {
    dispatch(setMaxAddressAmount(resp.data.max_address_amount))
  }

  if (
    (resp.data.address_quota === 0 || resp.data.address_quota) &&
    !Number.isNaN(resp.data.address_quota)
  ) {
    dispatch(setAddressQuota(resp.data.address_quota))
  }

  if (successCallback) {
    successCallback()
  }
}

export const deleteBlockchainAddress = (
  accountId: number,
  address: BlockchainAddress,
  onSuccess?: () => void
) => async (dispatch: Dispatch<ActionTypes>) => {
  const queryParams = objectToQueryParams({ blockchain: address.blockchain })
  const addressId = address.id
  const subRoute = `/${accountId}/${addressId}?${queryParams}`
  await del(ENDPOINTS.API_V3_STABLECOIN_ADDRESSES, {}, subRoute)

  dispatch(fetchBlockchainAddresses(accountId, onSuccess))
}

export const setBlockchainAddressDeletion = (blockchainAddressDeletion: boolean) => (
  dispatch: Dispatch<ActionTypes>
) => {
  dispatch({
    type: BLOCKCHAIN_ADDRESS_ACTIONS.SET_BLOCKCHAIN_ADDRESS_DELETION,
    blockchainAddressDeletion,
  })
}

export const setNewlyCreatedBlockchainAddress = (address: BlockchainAddress) => (
  dispatch: Dispatch<ActionTypes>
) => {
  dispatch({
    type: BLOCKCHAIN_ADDRESS_ACTIONS.SET_NEWLY_CREATED_BLOCKCHAIN_ADDRESS,
    blockchainAddress: address,
  })
}

export const updateVerifiedBlockchainAddress = (address: BlockchainAddress) => (
  dispatch: Dispatch<ActionTypes>
) => {
  dispatch({
    type: BLOCKCHAIN_ADDRESS_ACTIONS.UPDATE_VERIFIED_BLOCKCHAIN_ADDRESS,
    blockchainAddress: address,
  })
}

export const createBlockchainAddress = (
  isWhitelistingImprovementEnabled: boolean,
  addressCustodialType: string,
  blockchain: string,
  nickname: string,
  provider: string,
  address: string,
  accountId?: number,
  successCallback?: (address: BlockchainAddress) => void,
  errorCallback?: (errorMessage: string) => void
) => (dispatch: Dispatch<ActionTypes>) => {
  post(
    ENDPOINTS.API_V3_STABLECOIN_ADDRESSES,
    {
      address_custodial_type: addressCustodialType,
      blockchain,
      nickname,
      provider,
      address,
    },
    {},
    `/${accountId}`
  )
    .then(resp => {
      if (successCallback) {
        if (isWhitelistingImprovementEnabled && accountId) {
          const fetchBlockchainAddressesSuccessCallback = () => {
            const blockchainAddresses: BlockchainAddress[] = resp.data.filter(
              (obj: BlockchainAddress) => obj.address === address && obj.blockchain === blockchain
            )
            if (blockchainAddresses.length > 0) {
              successCallback(keysToCamel(blockchainAddresses[0]))
            } else {
              const blockchainAddress: BlockchainAddress = keysToCamel(resp.data[0])
              successCallback(blockchainAddress)
            }
          }
          dispatch(fetchBlockchainAddresses(accountId, fetchBlockchainAddressesSuccessCallback))
        } else {
          const blockchainAddress: BlockchainAddress = keysToCamel(resp)
          successCallback(blockchainAddress)
        }
      }
    })
    .catch((error: { response: { data: { error: string } } }) => {
      if (errorCallback) {
        errorCallback(error?.response?.data?.error)
      }
    })
}

export const setIsWhitelistingImprovementEnabled = (enabled: boolean) => (
  dispatch: Dispatch<ActionTypes>
) => {
  dispatch({
    type: BLOCKCHAIN_ADDRESS_ACTIONS.SET_IS_WHITELISTING_IMPROVEMENT_ENABLED,
    isWhitelistingImprovementEnabled: enabled,
  })
}
